import { Link } from "@tanstack/react-router";
import {
  Briefcase,
  Building2,
  Eye,
  GitCommitVertical,
  List,
  Pencil,
  SendHorizonal,
  Star,
  Users,
} from "lucide-react";
import { type ComponentType, useMemo, useState } from "react";
import {
  ConfirmViewAsElevatedDialog,
  viewAsNeedsConfirming,
} from "@/components/layout/ConfirmViewAsElevatedDialog";
import { PersonCapabilityGaps } from "@/components/persons/PersonCapabilityGaps";
import { PersonCertificationsSection } from "@/components/persons/PersonCertificationsSection";
import { PersonConstraintsSection } from "@/components/persons/PersonConstraintsSection";
import { PersonDirectReportsSection } from "@/components/persons/PersonDirectReportsSection";
import { PersonProjectsSection } from "@/components/persons/PersonProjectsSection";
import { PersonScheduledChangesSection } from "@/components/persons/PersonScheduledChangesSection";
import { PersonTimeline } from "@/components/persons/PersonTimeline";
import { PersonUpdateRequestDialog } from "@/components/persons/PersonUpdateRequestDialog";
import { PersonEmploymentTypeBadge, PersonStatusBadge } from "@/components/persons/person-badges";
import { ContactInfoCard } from "@/components/persons/person-profile/ContactInfoCard";
import { EmploymentCard } from "@/components/persons/person-profile/EmploymentCard";
import { RoleOverviewCard } from "@/components/persons/person-profile/RoleOverviewCard";
import { RolesPanel } from "@/components/persons/person-profile/RolesPanel";
import { roleIsCurrent } from "@/components/persons/person-profile/role-dates";
import { usePendingAdornments } from "@/components/persons/person-profile/use-pending-adornments";
import { GiveFeedbackDialog } from "@/components/reviews/GiveFeedbackDialog";
import { PersonReviewHistory } from "@/components/reviews/PersonReviewHistory";
import { ActivityLog } from "@/components/shared/activity-log";
import { DetailSection, MetadataRow } from "@/components/shared/detail-section";
import { type DetailMetaItem, EntityDetailHeader } from "@/components/shared/entity-detail-header";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { useMyUnseenFeedbackCount } from "@/hooks/use-adhoc-feedback";
import { useAppSettings } from "@/hooks/use-app-settings";
import { useImpersonation } from "@/hooks/use-impersonation";
import {
  type PersonDetail,
  useMyPerson,
  usePersonPendingChanges,
  usePersonRoles,
} from "@/hooks/use-persons";
import { useRoleClassificationOptions } from "@/hooks/use-role-classifications";
import { originLinkSearch } from "@/lib/detail-origin";
import { cn } from "@/lib/utils";
import { PersonAvatar } from "./PersonAvatar";
import { PersonIncentiveSection } from "./PersonIncentiveSection";
import { PersonPtoSection } from "./PersonPtoSection";

type TabKey =
  | "overview"
  | "projects"
  | "certifications"
  | "constraints"
  | "timeoff"
  | "scheduled"
  | "reviews"
  | "incentives"
  | "capabilities"
  | "history";

const BASE_TABS: TabItem[] = [
  { key: "overview", label: "Overview" },
  { key: "certifications", label: "Certifications" },
];
/**
 * Restricted to the person, their direct manager and admins as of 2026-09-22.
 *
 * This REVERSES the 2026-09-15 decision, which was that a constraint TAG is
 * readable by anyone signed in and redacting the 1-5 rating server-side was
 * enough. HR asked a second time and the stricter reading won: which
 * constraints someone carries is itself the private fact, not just how strongly
 * they hold them. Recorded rather than silently swapped, because the previous
 * comment existed for the same reason — so neither decision is undone by
 * someone who assumes it was an oversight.
 *
 * The server withholds the list for the same audience, so this only decides
 * whether to offer the tab.
 */
const CONSTRAINTS_TAB: TabItem = { key: "constraints", label: "Constraints" };
/**
 * Every review this person has ever had — EPIC-070. Same audience as
 * Projects/Constraints (the person, their direct manager, admins), plus
 * gated on the feature flag — the server's `reviewAudienceFor` is the real
 * check per review, this only decides whether to offer the tab at all.
 */
const REVIEWS_TAB: TabItem = { key: "reviews", label: "Reviews" };
/**
 * Where this person stands against what their role requires — EPIC-023.
 *
 * Same audience as Reviews, and for the same reason: the scores it shows ARE
 * review scores. The server re-checks every contributing review with
 * `reviewAudienceFor`, so this only decides whether to offer the tab.
 */
const CAPABILITIES_TAB: TabItem = { key: "capabilities", label: "Capabilities" };
/**
 * What projects someone is on, and their forward guidance. Restricted to the
 * person, their direct manager and admins as of 2026-09-15 (WI-119, AC-919) —
 * it used to be readable by anyone signed in. Absent entirely for anyone else
 * rather than present-and-empty, which would imply they are on no projects.
 */
const PROJECTS_TAB: TabItem = { key: "projects", label: "Current Projects" };
/**
 * Time off is shown to EVERYONE signed in, and has been since 2026-09-15
 * (WI-148): a colleague sees when someone is away so they can plan around it.
 * The tab narrows its own contents — the year's balances and anything awaiting
 * approval are withheld server-side for anyone outside the person, their direct
 * manager and admins.
 */
const TIME_OFF_TAB: TabItem = { key: "timeoff", label: "Time off" };
// The Scheduled-changes tab only exists while future-dated changes are queued;
// Change history is always last.
const SCHEDULED_TAB: TabItem = { key: "scheduled", label: "Scheduled changes" };
/**
 * A person's incentive attainment — read live from NOS, percentages only. Offered to
 * the person, their direct manager and People Ops (the same audience as
 * `canViewPrivateDetail`); the API enforces it independently and answers 403 to
 * anyone else, so this is only the offer. Reading someone else's is audited.
 */
const INCENTIVES_TAB: TabItem = { key: "incentives", label: "Incentives" };
const HISTORY_TAB: TabItem = { key: "history", label: "Change history" };

function isPersonTabKey(value: unknown): value is TabKey {
  return (
    value === "overview" ||
    value === "projects" ||
    value === "certifications" ||
    // Both were missing, so `?tab=constraints` and `?tab=timeoff` fell back to
    // Overview and neither tab could be linked to.
    value === "constraints" ||
    value === "timeoff" ||
    value === "scheduled" ||
    value === "reviews" ||
    value === "incentives" ||
    value === "capabilities" ||
    value === "history"
  );
}

/**
 * Normalize a `?tab=` value. `demands` was this tab's key while it was the
 * "Active Demands" stub — keep honouring it so any saved link still lands here.
 */
function personTabFrom(value: unknown): TabKey | null {
  if (value === "demands") return "projects";
  return isPersonTabKey(value) ? value : null;
}

interface PersonProfileProps {
  person: PersonDetail;
  isAdmin: boolean;
  /** Can see everything — the read tier. Wider than `isAdmin`, which is edit. */
  canView: boolean;
  /** True when the viewer is looking at their OWN person record. */
  isSelf: boolean;
  onEdit: () => void;
  /** Tab to open on mount — e.g. a back-link from a certification reopening "certifications". */
  initialTab?: string;
}

export function PersonProfile({
  person,
  isAdmin,
  canView,
  isSelf,
  onEdit,
  initialTab,
}: PersonProfileProps) {
  // Change history is scoped: anyone who can view everything sees it on any
  // profile, everyone else only on their own. Uses the VIEW tier, matching the
  // /api/audit-log authorization (canViewEntityAudit) — gating it on the edit
  // tier would hide history a view-only admin is allowed to read.
  const canViewHistory = canView || isSelf;
  const impersonation = useImpersonation();
  // Own profile and an active viewing session hide the affordance entirely (the
  // server refuses both). `isAdmin` is the EDIT tier here.
  const offerViewAs = isAdmin && !isSelf && !impersonation.active;
  // A person with no sign-in account can be viewed as too: the server creates a
  // credential-less one on demand (WI-150). The only case left that cannot is
  // someone with no WORK EMAIL — that address is the join key between an
  // account and an employee record, so there would be nothing to attach. Two of
  // 94 active people are in that state, and they get the button disabled and
  // explained rather than a click that fails.
  const canViewAs = offerViewAs && Boolean(person.email?.trim());
  const [tab, setTab] = useState<TabKey>(() => {
    const initial = personTabFrom(initialTab) ?? "overview";
    return initial === "history" && !canViewHistory ? "overview" : initial;
  });
  const [requestingUpdate, setRequestingUpdate] = useState(false);
  // Set only when the account we resolved holds admin access, so the click
  // pauses on a confirm instead of switching identity straight away — see
  // `ConfirmViewAsElevatedDialog`.
  const [confirmViewAs, setConfirmViewAs] = useState<{
    userId: string;
    role: string | null;
  } | null>(null);
  const { data: roles = [] } = usePersonRoles(person.id);
  const pendingQuery = usePersonPendingChanges(person.id);
  const pending = pendingQuery.data ?? [];
  /**
   * The tab appears when there ARE queued changes, or when we could not find
   * out.
   *
   * A failed read used to be indistinguishable from "nothing is queued", so the
   * tab simply vanished from a profile that did have a change waiting and the
   * admin re-entered it. The section itself renders the error.
   */
  const hasPending = pending.length > 0 || pendingQuery.isError;
  // Manager-ness is decided against the viewer's OWN person record: a profile's
  // `managerId` names a person, and the session names a user, so the two cannot
  // be compared directly. Server-side authorization is the real gate — this
  // only decides whether to offer the tab.
  const { data: myPerson } = useMyPerson();
  // `useMyPerson` resolves to null for a signed-in user with no person record.
  const isTheirManager = person.managerId !== null && person.managerId === myPerson?.id;
  // The person, their direct manager and admins — the same audience the server
  // enforces as `personRelation() !== "other"`. Time off is no longer gated on
  // it (everyone sees upcoming leave); Current Projects now is.
  const canViewPrivateDetail = canView || isSelf || isTheirManager;
  // Same flag the sidebar uses to decide whether to show Reviews at all —
  // not a security boundary, the server 404s every /api/reviews* route
  // while the feature is off.
  const { data: appSettings } = useAppSettings();
  const reviewsAvailable = appSettings?.reviewsEnabled ?? false;
  // Your own new feedback — shared with you since you last opened Reviews.
  // Never fetched for someone else's profile: the count is always the viewer's.
  const { data: unseenFeedback } = useMyUnseenFeedbackCount(reviewsAvailable && isSelf);
  const tabs: TabItem[] = [...BASE_TABS];
  if (canViewPrivateDetail) tabs.splice(1, 0, PROJECTS_TAB);
  if (canViewPrivateDetail) tabs.push(CONSTRAINTS_TAB);
  tabs.push(TIME_OFF_TAB);
  if (hasPending) tabs.push(SCHEDULED_TAB);
  if (reviewsAvailable && canViewPrivateDetail)
    tabs.push(isSelf ? { ...REVIEWS_TAB, count: unseenFeedback } : REVIEWS_TAB);
  if (canViewPrivateDetail) tabs.push(CAPABILITIES_TAB);
  if (canViewPrivateDetail) tabs.push(INCENTIVES_TAB);
  if (canViewHistory) tabs.push(HISTORY_TAB);
  // If the tab currently selected disappears — the last queued change is
  // canceled/comes due, or the viewer may not see it — fall back to the
  // overview instead of a blank panel. Asked of `tabs` rather than by listing
  // the conditions again: every entry above already decides its own audience,
  // and restating them here is how a newly-gated tab (Constraints, 2026-09-22)
  // gets missed.
  const activeTab: TabKey = tabs.some((t) => t.key === tab) ? tab : "overview";
  // The header role reflects who they are *now* — ignore roles that have ended
  // or haven't started yet.
  const currentRoles = roles.filter(roleIsCurrent);
  const primaryRole = currentRoles.find((r) => r.isPrimary) ?? currentRoles[0];

  // Header meta: primary role, role team, department. Job title stays the
  // header subtitle; work email + role classification moved off the header.
  const meta: DetailMetaItem[] = [];
  if (primaryRole) {
    meta.push({
      key: "role",
      icon: Briefcase,
      node: (
        <Link
          to="/dashboard/roles/$roleId"
          params={{ roleId: primaryRole.roleId }}
          search={originLinkSearch("person", person.id, person.name)}
          className="font-medium text-primary underline decoration-primary/40 underline-offset-2 transition-colors hover:decoration-primary"
        >
          {primaryRole.roleName}
          {primaryRole.roleLevelName && (
            // The level is part of the role's identity ("Engineer — Senior"),
            // but de-emphasised so the role name still leads.
            <span className="font-normal text-muted-foreground">
              {" "}
              — {primaryRole.roleLevelName}
            </span>
          )}
        </Link>
      ),
    });
  }
  if (person.roleTeamName) meta.push({ key: "team", icon: Users, node: person.roleTeamName });
  if (person.departmentName)
    meta.push({ key: "dept", icon: Building2, node: person.departmentName });

  return (
    // data-wide-page lifts the dashboard's 72rem content cap so the profile can use
    // a wide screen — its tabs hold tables and grids that read badly squeezed. The
    // cap that keeps panels off the screen edges is on the route (`detail.tsx`),
    // so the back link and the profile centre together.
    <div className="space-y-4" data-wide-page>
      {/* Header + tabs stay pinned to the top of the scrolling main area while
          the profile body scrolls beneath. The negative margins let the
          backdrop span the full width of main's padding.

          Two things keep scrolled content from showing through — a long tab
          body (the Change history timeline especially) used to ghost through
          the header and re-appear above it:
            - The background is fully opaque (it was bg-background/80 + blur).
            - The ::before covers main's top padding. A stuck box is clamped to
              the scrollport's *padding* box, so it can't be pulled over that
              strip with a negative margin — content scrolled up through the
              gap and stayed visible there. The filler heights mirror main's
              p-4 / md:p-6. */}
      <div className="sticky top-0 z-20 -mx-4 space-y-4 bg-background px-4 pb-2 pt-1 before:absolute before:inset-x-0 before:bottom-full before:h-4 before:bg-background md:-mx-6 md:px-6 md:before:h-6">
        <EntityDetailHeader
          visual={
            <PersonAvatar
              name={person.name}
              seed={person.id}
              size="xl"
              imageUrl={person.avatarUrl}
            />
          }
          title={person.name}
          subtitle={person.title}
          meta={meta}
          badges={
            <>
              <PersonStatusBadge status={person.status} />
              <PersonEmploymentTypeBadge employmentType={person.employmentType} />
              {/* Names the team rather than reading a bare "Team Lead": the meta
                  strip below already shows the team this person BELONGS to, and
                  the two are not necessarily the same thing. */}
              {person.ledRoleTeamName && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-2xs text-primary">
                  <Star className="size-3 fill-current" />
                  Lead — {person.ledRoleTeamName}
                </span>
              )}
            </>
          }
          actions={
            <>
              {/* Own profile: request a change (e.g. manager) that isn't
                  self-serviceable — emailed to the records team. */}
              {isSelf && (
                <Button variant="outline" size="sm" onClick={() => setRequestingUpdate(true)}>
                  <SendHorizonal className="mr-1.5 size-3.5" />
                  Request update
                </Button>
              )}
              {/* Any colleague, about any OTHER colleague — EPIC-067 WI-243. */}
              {!isSelf && reviewsAvailable && (
                <GiveFeedbackDialog initialSubjectPersonId={person.id} />
              )}
              {offerViewAs &&
                (canViewAs ? (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={impersonation.isPending}
                    onClick={() => {
                      void (async () => {
                        const account = await impersonation.prepareForPerson(person.id);
                        if (account === null) return;
                        if (viewAsNeedsConfirming(account.role)) {
                          setConfirmViewAs(account);
                          return;
                        }
                        await impersonation.start(account.userId);
                      })();
                    }}
                  >
                    <Eye className="mr-1.5 size-3.5" />
                    View as this person
                  </Button>
                ) : (
                  // The span carries the hover: a disabled button fires no
                  // pointer events, so a tooltip anchored on it never opens.
                  <SimpleTooltip content="This person has no work email on record, so there is nothing to attach a sign-in account to. Add their work email first.">
                    <span className="inline-flex">
                      <Button variant="outline" size="sm" disabled>
                        <Eye className="mr-1.5 size-3.5" />
                        View as this person
                      </Button>
                    </span>
                  </SimpleTooltip>
                ))}
              {isAdmin && (
                <Button size="sm" onClick={onEdit}>
                  <Pencil className="mr-1.5 size-3.5" />
                  Edit
                </Button>
              )}
            </>
          }
        />

        <TabBar
          variant="folder"
          tabs={tabs}
          value={activeTab}
          onChange={(k) => setTab(k as TabKey)}
        />
      </div>

      {isSelf && (
        <PersonUpdateRequestDialog open={requestingUpdate} onOpenChange={setRequestingUpdate} />
      )}

      {confirmViewAs !== null && (
        <ConfirmViewAsElevatedDialog
          open
          onOpenChange={(next) => {
            if (!next) setConfirmViewAs(null);
          }}
          name={person.name}
          role={confirmViewAs.role}
          onConfirm={() => {
            void impersonation.start(confirmViewAs.userId);
            setConfirmViewAs(null);
          }}
        />
      )}

      {activeTab === "overview" && (
        <OverviewTab person={person} isAdmin={isAdmin} canView={canView} isSelf={isSelf} />
      )}
      {activeTab === "projects" && (
        <PersonProjectsSection personId={person.id} personName={person.name} />
      )}
      {activeTab === "constraints" && (
        <PersonConstraintsSection personId={person.id} personName={person.name} />
      )}
      {activeTab === "certifications" && (
        <PersonCertificationsSection
          personId={person.id}
          personName={person.name}
          isAdmin={isAdmin}
        />
      )}
      {activeTab === "timeoff" && (
        <PersonPtoSection personId={person.id} personName={person.name} />
      )}
      {activeTab === "scheduled" && (
        <PersonScheduledChangesSection
          person={person}
          isAdmin={isAdmin}
          pending={pending}
          failed={pendingQuery.isError}
          onRetry={() => pendingQuery.refetch()}
        />
      )}
      {activeTab === "reviews" && <PersonReviewHistory personId={person.id} />}
      {activeTab === "incentives" && (
        <PersonIncentiveSection personId={person.id} personName={person.name} isSelf={isSelf} />
      )}
      {activeTab === "capabilities" && <PersonCapabilityGaps personId={person.id} />}
      {activeTab === "history" && <HistoryTab person={person} />}
    </div>
  );
}

function OverviewTab({
  person,
  isAdmin,
  canView,
  isSelf,
}: {
  person: PersonDetail;
  isAdmin: boolean;
  canView: boolean;
  isSelf: boolean;
}) {
  // Same rule as the Change history tab — see `canViewHistory` above.
  const canViewHistory = canView || isSelf;
  // One query, shared by all four cards: which fields have a change queued.
  const adorn = usePendingAdornments(person.id);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
      <div className="space-y-6">
        <ContactInfoCard person={person} adorn={adorn} canView={canView} isSelf={isSelf} />
        <RoleOverviewCard
          person={person}
          adorn={adorn}
          isAdmin={isAdmin}
          canViewHistory={canViewHistory}
        />
        <EmploymentCard person={person} adorn={adorn} canView={canView} />
        {/* Admin notes are ABOUT a person, not theirs — no self carve-out (see
            `ADMIN_ONLY_PERSON_FIELDS`). The server nulls the value below the
            read tier; this hides the section with it. */}
        {canView && (
          <DetailSection title="Notes" boldTitle className="gap-4 py-5">
            <MetadataRow label="Notes" value={person.notes} adornment={adorn("notes")} />
          </DetailSection>
        )}
      </div>

      <div className="space-y-6">
        <RolesPanel person={person} adorn={adorn} isAdmin={isAdmin} />
        <PersonDirectReportsSection
          personId={person.id}
          personName={person.name}
          canViewTeam={isAdmin || isSelf}
        />
      </div>
    </div>
  );
}

const HISTORY_FIELD_LABELS: Record<string, string> = {
  title: "Job title",
  email: "Work email",
  personalEmail: "Personal email",
  entityName: "Entity name",
  // Current + pre-rename keys — old audit rows still carry "capacity".
  employmentType: "Employment type",
  capacity: "Employment type",
  startDate: "Start date",
  rehireDate: "Rehire date",
  endDate: "End date",
  vcStartDate: "VC Start Date",
  employeeProfileId: "Employee Profile",
  hoursPerWeek: "Hours per week",
  workingHoursStart: "Working hours start",
  workingHoursEnd: "Working hours end",
  workingHoursStart2: "Working hours start (2nd set)",
  workingHoursEnd2: "Working hours end (2nd set)",
  workingHoursNote: "Working hours note",
  roleClassification: "Role Classification",
  userId: "Linked account",
  departmentId: "Department",
  roleTeamId: "Role Team",
  managerId: "Manager",
  // Synthetic key written by the manager-history span editor (add / edit /
  // delete), so those corrections show up on the person's own history.
  managerHistory: "Manager history",
  dottedLineHistory: "Dotted line history",
  dottedLineId: "Dotted line",
  roleTeamLeadRoleId: "Role",
  aspirationalRoles: "Aspirational roles",
};

type HistoryView = "timeline" | "list";

function HistoryTab({ person }: { person: PersonDetail }) {
  // The formatted timeline is the default view; the flat list stays one click
  // away for anyone who prefers the raw chronological stream.
  const [view, setView] = useState<HistoryView>("timeline");
  // Role classification is admin-managed config, so its label comes from the
  // table rather than a module constant. This is a belt-and-braces fallback:
  // the server already resolves audit diffs (`kind: "config"` in
  // `audit-resolve.ts`), so an already-resolved name arrives here, misses the
  // lookup, and passes through unchanged — which is the correct outcome.
  const classifications = useRoleClassificationOptions();
  const fieldResolvers = useMemo<Record<string, (value: unknown) => string>>(
    () => ({
      roleClassification: (v) =>
        typeof v === "string" ? classifications.labelFor(v) : String(v ?? ""),
    }),
    [classifications],
  );
  return (
    <DetailSection
      title="Change history"
      boldTitle
      headerActions={<HistoryViewToggle value={view} onChange={setView} />}
    >
      {view === "timeline" ? (
        <PersonTimeline
          entityType="person"
          entityId={person.id}
          fieldLabels={HISTORY_FIELD_LABELS}
          fieldResolvers={fieldResolvers}
        />
      ) : (
        <ActivityLog
          entityType="person"
          entityId={person.id}
          fieldLabels={HISTORY_FIELD_LABELS}
          fieldResolvers={fieldResolvers}
        />
      )}
    </DetailSection>
  );
}

function HistoryViewToggle({
  value,
  onChange,
}: {
  value: HistoryView;
  onChange: (next: HistoryView) => void;
}) {
  return (
    <div className="inline-flex items-center rounded-lg border p-0.5">
      <HistoryViewOption
        active={value === "timeline"}
        label="Timeline"
        icon={GitCommitVertical}
        onClick={() => onChange("timeline")}
      />
      <HistoryViewOption
        active={value === "list"}
        label="List"
        icon={List}
        onClick={() => onChange("list")}
      />
    </div>
  );
}

function HistoryViewOption({
  active,
  label,
  icon: Icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={`${label} view`}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors",
        active ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
